import { useEffect } from "react";
import dayjs from "@shared/dayjs";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "@shared/v2/IconButton";
import { PencilSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";
import { TBody, TD, TH, THead, TR, Table } from "../../Reports/components/BusinessDashboard/Table/Table";
import AvatarLink from "../AvatarLink";
import TemplatesPagination from "../TemplatesPagination";
import { THEAD_CLASSES } from "../types";
import InteractionTemplateModal from "./InteractionTemplateModal";
import {
  getInteractionTemplates,
  setDeleteTemplate,
  setEditTemplate,
} from "../../reducers/interactionTemplatesReducer";
import DeleteInteractionTemplateModal from "./DeleteInteractionTemplateModal";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";
import TemplatesToolbar from "../TemplatesToolbar";
import useTemplateSearchParams from "../useTemplateSearchParams";
import type { InteractionTemplatesState } from "./types";

interface CurrentUser {
  id: number;
  account: { isAdmin: boolean };
}

const InteractionTemplates = () => {
  const currentUser = useCurrentUser() as CurrentUser;
  const dispatch = useDispatch();
  const { page, urlSearch, search, setSearch, updateParams } = useTemplateSearchParams();
  const {
    loading,
    data: interactionTemplates,
    pagination,
  } = useSelector((state: InteractionTemplatesState) => state.interactionTemplates.interactionTemplates);

  useEffect(() => {
    // @ts-expect-error untyped thunk
    dispatch(getInteractionTemplates({ params: { page, search: urlSearch } }));
  }, [page, urlSearch]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-[16px] tw-flex-1 tw-min-h-0">
      <TemplatesToolbar
        search={search}
        onSearchChange={setSearch}
        loading={loading}
        createLabel="Create New Email Template"
        onCreate={() => dispatch(setEditTemplate({ name: "", subject: "", body: "" }))}
      />
      <div className="tw-flex-1 tw-overflow-auto">
        <Table className="tw-border-separate">
          <THead className="tw-bg-white tw-sticky tw-top-0">
            {/* @ts-expect-error props */}
            <TR>
              {/* @ts-expect-error props */}
              <TH className={THEAD_CLASSES}>Title</TH>
              {/* @ts-expect-error props */}
              <TH className={`tw-text-center ${THEAD_CLASSES}`}>Created</TH>
              {/* @ts-expect-error props */}
              <TH className={`tw-text-center ${THEAD_CLASSES}`}>Created By</TH>
              {/* @ts-expect-error props */}
              <TH className={`tw-text-center ${THEAD_CLASSES}`}>Last Updated</TH>
              {/* @ts-expect-error props */}
              <TH className={`tw-text-center ${THEAD_CLASSES}`}>Updated By</TH>
              {/* @ts-expect-error props */}
              <TH className={`tw-text-center ${THEAD_CLASSES}`}>Edit</TH>
            </TR>
          </THead>
          {/* @ts-expect-error props */}
          <TBody>
            {interactionTemplates.map((template) => {
              const canEdit = currentUser.account.isAdmin || currentUser.id === template.createdById;
              return (
                // @ts-expect-error props
                <TR key={template.uuid} className="tw-border-0 hover:tw-bg-gray-5">
                  {/* @ts-expect-error props */}
                  <TD>{template.name}</TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center">{dayjs(template.createdAt).format("MMM DD, YYYY")}</TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center">
                    <AvatarLink simplePerson={template.createdBy} />
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center">{dayjs(template.updatedAt).fromNow()}</TD>
                  {/* @ts-expect-error props */}
                  <TD className="tw-text-center">
                    <AvatarLink simplePerson={template.updatedBy} />
                  </TD>
                  {/* @ts-expect-error props */}
                  <TD>
                    <div className="tw-flex tw-gap-[4px] tw-justify-center tw-items-center">
                      {/* @ts-expect-error props */}
                      <IconButton
                        disabled={!canEdit}
                        aria-label="Edit"
                        schema="tertiary"
                        onClick={() => dispatch(setEditTemplate(template))}
                      >
                        <PencilSolidV6 />
                      </IconButton>
                      {/* @ts-expect-error props */}
                      <IconButton
                        disabled={!canEdit}
                        aria-label="Delete"
                        schema="tertiary"
                        onClick={() => dispatch(setDeleteTemplate(template))}
                      >
                        <TrashCanSolidV6 />
                      </IconButton>
                    </div>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </div>

      {interactionTemplates.length > 0 && (
        <TemplatesPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onGoToPage={(nextPage) => updateParams({ page: nextPage })}
        />
      )}

      <DeleteInteractionTemplateModal />
      <InteractionTemplateModal />
    </div>
  );
};

export default InteractionTemplates;
